import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { inrExact } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, TrendingUp, Wallet, Trophy, Users } from "lucide-react";

const Stat = ({ label, value, accent }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-5 py-4">
    <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
    <div className={`font-mono font-bold text-xl mt-1 ${accent || "text-slate-900"}`}>{value}</div>
  </div>
);

export default function MelaRoi() {
  const navigate = useNavigate();
  const [data, setData] = useState({ melas: [], totals: {} });

  useEffect(() => { api.get("/melas/roi").then((r) => setData(r.data)); }, []);
  const { melas, totals } = data;
  const best = melas.reduce((b, m) => (m.cost_per_loan && (!b || m.cost_per_loan < b.cost_per_loan) ? m : b), null);

  return (
    <div data-testid="mela-roi-page">
      <button onClick={() => navigate("/app/mela")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3" data-testid="roi-back-btn">
        <ArrowLeft size={15} /> Back to Loan Mela
      </button>
      <PageHeader title="Mela ROI Report" subtitle="Spend, conversions and cost-per-loan across every mela." />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat label="Total Spent" value={inrExact(totals.spend || 0)} accent="text-[#0F4C81]" />
        <Stat label="Loans (Disbursed)" value={totals.loans || 0} accent="text-emerald-600" />
        <Stat label="Cost / Loan" value={totals.loans ? inrExact(totals.cost_per_loan) : "—"} />
        <Stat label="Overall Conversion" value={`${totals.conversion || 0}%`} />
      </div>

      {best && (
        <div className="flex items-center gap-2 mb-4 text-sm text-slate-600 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2.5" data-testid="roi-best-mela">
          <Trophy size={16} className="text-emerald-600" />
          Best performer: <b className="text-slate-900">{best.name}</b> at <b className="font-mono">{inrExact(best.cost_per_loan)}</b> per disbursed loan.
        </div>
      )}

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Mela</TableHead>
              <TableHead className="text-right">Invited</TableHead>
              <TableHead className="text-right">Attended</TableHead>
              <TableHead className="text-right">Loans</TableHead>
              <TableHead className="text-right">Conv.</TableHead>
              <TableHead className="text-right">Spend</TableHead>
              <TableHead className="text-right">Cost / Attendee</TableHead>
              <TableHead className="text-right">Cost / Loan</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {melas.map((m) => (
              <TableRow key={m.id} data-testid={`roi-row-${m.id}`}>
                <TableCell>
                  <div className="font-semibold text-slate-900">{m.name}</div>
                  <div className="text-xs text-slate-400">{m.date}{m.venue ? ` · ${m.venue}` : ""}</div>
                </TableCell>
                <TableCell className="text-right font-mono text-sm">{m.invited}</TableCell>
                <TableCell className="text-right font-mono text-sm text-[#0F4C81]">{m.attended}</TableCell>
                <TableCell className="text-right font-mono text-sm text-emerald-600 font-semibold">{m.loans}</TableCell>
                <TableCell className="text-right font-mono text-sm">{m.conversion}%</TableCell>
                <TableCell className="text-right font-mono text-sm">{inrExact(m.spend)}</TableCell>
                <TableCell className="text-right font-mono text-sm">{m.attended ? inrExact(m.cost_per_attendee) : "—"}</TableCell>
                <TableCell className="text-right font-mono text-sm font-semibold">{m.loans ? inrExact(m.cost_per_loan) : "—"}</TableCell>
              </TableRow>
            ))}
            {melas.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-400 py-10">No melas yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
      <p className="text-xs text-slate-400 mt-3 flex items-center gap-1.5"><TrendingUp size={13} />A "loan" counts a mela attendee whose pipeline status reached <b>Disbursed</b>. Conversion = loans ÷ attended.</p>
    </div>
  );
}
